<script setup lang="ts">
import { useId } from 'vue'
import { useI18n } from 'vue-i18n'
import ViewerMouseGlyph from '@/components/bop/ViewerMouseGlyph.vue'
import ViewerTouchGlyph from '@/components/bop/ViewerTouchGlyph.vue'
import useViewerControlsHelp from '@/composables/useViewerControlsHelp'
import useCoarsePointer from '@/composables/useCoarsePointer'

const { t } = useI18n()
const { open, toggle, hide } = useViewerControlsHelp()
const touch = useCoarsePointer()
const panelId = useId()
</script>

<template>
  <v-btn
    icon
    size="small"
    variant="tonal"
    :color="open ? 'primary' : 'grey-darken-2'"
    :title="
      open
        ? t('component.bopModelViewer.controlsHelpHide')
        : t('component.bopModelViewer.controlsHelpShow')
    "
    :aria-expanded="open"
    :aria-controls="panelId"
    @click="toggle"
  >
    <v-icon>help_outline</v-icon>
  </v-btn>

  <transition name="viewer-controls-help">
    <section
      v-if="open"
      :id="panelId"
      class="viewer-controls-help"
      :aria-label="t('component.bopModelViewer.controlsHelpTitle')"
    >
      <div class="viewer-controls-help__header">
        <span class="viewer-controls-help__title">
          {{ t('component.bopModelViewer.controlsHelpTitle') }}
        </span>
        <v-btn
          icon
          size="x-small"
          variant="text"
          color="grey-darken-2"
          :title="t('component.bopModelViewer.controlsHelpHide')"
          :aria-label="t('component.bopModelViewer.controlsHelpHide')"
          @click="hide"
        >
          <v-icon size="16">close</v-icon>
        </v-btn>
      </div>

      <dl class="viewer-controls-help__list">
        <div>
          <dt>
            <viewer-touch-glyph v-if="touch" gesture="one-finger" />
            <viewer-mouse-glyph v-else button="left" />
            {{ t('component.bopModelViewer.controlRotate') }}
          </dt>
          <dd>
            {{
              touch
                ? t('component.bopModelViewer.controlRotateTouch')
                : t('component.bopModelViewer.controlRotateGesture')
            }}
          </dd>
        </div>
        <div>
          <dt>
            <viewer-touch-glyph v-if="touch" gesture="two-fingers" />
            <viewer-mouse-glyph v-else button="right" />
            {{ t('component.bopModelViewer.controlPan') }}
          </dt>
          <dd v-if="touch">{{ t('component.bopModelViewer.controlPanTouch') }}</dd>
          <dd v-else>
            {{ t('component.bopModelViewer.controlPanGesture') }}
            <i18n-t
              keypath="component.bopModelViewer.controlPanAlternative"
              tag="span"
              class="viewer-controls-help__alternative"
            >
              <template #key>
                <kbd>Shift</kbd>
              </template>
            </i18n-t>
          </dd>
        </div>
        <div>
          <dt>
            <viewer-touch-glyph v-if="touch" gesture="pinch" />
            <viewer-mouse-glyph v-else button="wheel" />
            {{ t('component.bopModelViewer.controlZoom') }}
          </dt>
          <dd>
            {{
              touch
                ? t('component.bopModelViewer.controlZoomTouch')
                : t('component.bopModelViewer.controlZoomGesture')
            }}
          </dd>
        </div>
      </dl>
    </section>
  </transition>
</template>

<style scoped>
.viewer-controls-help {
  position: relative;
  isolation: isolate;
  width: max-content;
  max-width: 260px;
  padding: 4px 6px 10px 12px;
  border-radius: 12px;
  font-size: 0.8em;
  color: rgba(0, 0, 0, 0.75);
}

.viewer-controls-help::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: -1;
  border-radius: inherit;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transform-origin: top right;
}

.viewer-controls-help__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.viewer-controls-help__title {
  font-size: 0.9em;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.6);
}

.viewer-controls-help__list {
  display: grid;
  grid-template-columns: auto auto;
  column-gap: 16px;
  row-gap: 8px;
  margin: 2px 6px 0 0;
}

.viewer-controls-help__list > div {
  display: contents;
}

.viewer-controls-help__list dt {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
}

.viewer-controls-help__list dd {
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 0;
  color: rgba(0, 0, 0, 0.62);
}

.viewer-controls-help__alternative {
  font-size: 0.92em;
}

.viewer-controls-help kbd {
  padding: 0 4px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-bottom-width: 2px;
  border-radius: 4px;
  background: #fff;
  font: inherit;
  font-size: 0.9em;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.75);
}

.viewer-controls-help-enter-active {
  animation: viewer-controls-help-fade-in 200ms cubic-bezier(0.16, 1, 0.3, 1) both;
}

.viewer-controls-help-enter-active::before {
  animation: viewer-controls-help-unfold 200ms cubic-bezier(0.16, 1, 0.3, 1) both;
}

.viewer-controls-help-leave-active {
  animation: viewer-controls-help-fade-out 120ms cubic-bezier(0.7, 0, 0.84, 0) both;
}

.viewer-controls-help-leave-active::before {
  animation: viewer-controls-help-fold 120ms cubic-bezier(0.7, 0, 0.84, 0) both;
}

@keyframes viewer-controls-help-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes viewer-controls-help-fade-out {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

@keyframes viewer-controls-help-unfold {
  from {
    transform: scale(0.6);
  }
  to {
    transform: none;
  }
}

@keyframes viewer-controls-help-fold {
  from {
    transform: none;
  }
  to {
    transform: scale(0.8);
  }
}

@media (prefers-reduced-motion: reduce) {
  .viewer-controls-help-enter-active,
  .viewer-controls-help-enter-active::before,
  .viewer-controls-help-leave-active,
  .viewer-controls-help-leave-active::before {
    animation: none;
  }
}
</style>
